import { Component, Input, OnInit, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeleteReasonRemarkComponent } from 'src/app/Account/delete-reason-remark/delete-reason-remark.component';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { ALERT_TYPE } from 'src/app/constants';
import { CustomerTransactionsComponent } from 'src/app/Customer/customer-transactions/customer-transactions.component';
import { AddReceiptSetComponent } from 'src/app/Sets/add-receipt-set/add-receipt-set.component';
import { ReceiptDetailsComponent } from 'src/app/Sets/receipt-details/receipt-details.component';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Account } from 'src/model/account.model';
import { Transaction } from 'src/model/transaction.model';

interface ReportRecord {
	date?: Date;
	remark?: string;
	amount: number;
	receiptNumber?: string;
	receiptId?: number;
	type?: number;
	customerName?: string;
	customerCode?: string;
	customerId?: number;
	profileId?: number;
	transactionId?: number;
	containsExtra?: boolean;
	extrasAmount?: number;
	groupId?: number;
	chargesAmount?: number;
	setId?: number;
	cashbookRelatedAmount?: number;
	nonCashbookRelatedTransactions?: Transaction[];
}

@Component({
	selector: 'app-daily-report',
	templateUrl: './daily-report.component.html',
	styleUrls: ['./daily-report.component.scss'],
	standalone: false
})
export class DailyReportComponent implements OnInit, OnDestroy {
	protected enums = inject(Enums);
	private activatedRoute = inject(ActivatedRoute);
	private router = inject(Router);
	private restapi = inject(RestApiService);
	private constructApi = inject(ConstructAPI);
	protected sharedService = inject(SharedService);

	@Input() selectedCompany: number;
	@Input() selectedReport: number;
	@Input() startDate: Date;

	totalCashBalance = 0;

	reportList: ReportRecord[] = [];
	loanReportList: ReportRecord[] = [];
	isCollapsed = false;

	companyBankAccountList: Account[] = [];
	cashAccountList: Account[] = [];

	routeSubscription: any;

	ngOnInit(): void {
		this.routeSubscription = this.activatedRoute.paramMap.subscribe(params => {
			this.selectedCompany = +params.get('companyId');
			this.startDate = params.get('startDate') == undefined ? undefined : new Date(params.get('startDate'));
			this.selectedReport = +params.get('reportId');

			if (this.startDate != undefined && this.selectedReport != undefined) {
				this.getLoansTransaction();
				this.getOtherTranscations();

				this.getLastBalance(this.selectedCompany);
			}

			if (this.sharedService.isBranchAccountHandler()) this.getAllAccountsOfCompany(this.selectedCompany);
		});
	}

	private getAllAccountsOfCompany(companyId: number) {
		const xhttp = this.restapi.getRequest(this.constructApi.getAccountsOfCompany(companyId));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);

				jsonArray.forEach(json => {
					const amount = json.balance == undefined ? 0 : json.balance;
					const account = new Account(json.id, json.number, json.name, json.holderName, amount, json.companyId, json.username);

					if (account.Username != undefined) this.cashAccountList.push(account);
					else {
						this.companyBankAccountList.push(account);
					}
				});
			}
		};
	}

	getPositiveNegative(amount) {
		if (amount > 0) return 'text-success';
		else return 'text-danger';
	}
	ngOnDestroy() {
		this.routeSubscription.unsubscribe();
	}

	goToCustomerProfile(selectedCustomerID: number, selectedProfileId: number) {
		this.router.navigate(['/manageCustomer', selectedCustomerID, selectedProfileId]);
	}

	goToProfileTransaction(profileId: number) {
		const modalRef = this.sharedService.openModal(CustomerTransactionsComponent, 'largeModal');
		modalRef.componentInstance.profileId = profileId;
		modalRef.componentInstance.startDate = this.startDate;
		modalRef.componentInstance.endDate = this.startDate;
		modalRef.componentInstance.companyId = this.selectedCompany;
	}

	calculateTotal() {
		let total = 0;

		for (let i = 0; i < this.loanReportList.length; i++) {
			total += this.loanReportList[i].amount;
		}

		return total;
	}

	calculateTotalCharges() {
		let total = 0;

		for (let i = 0; i < this.loanReportList.length; i++) {
			if (this.loanReportList[i].chargesAmount != undefined) total += this.loanReportList[i].chargesAmount;
		}

		return total;
	}

	calculateTotalCashbookRelatedAmount() {
		let total = 0;

		for (let i = 0; i < this.loanReportList.length; i++) {
			if (this.loanReportList[i].cashbookRelatedAmount != undefined) total += this.loanReportList[i].cashbookRelatedAmount;
		}

		return total;
	}

	calculateTotalOtherTransaction() {
		let total = 0;

		for (let i = 0; i < this.reportList.length; i++) {
			total += this.reportList[i].amount;
		}

		return total;
	}
	calculateTotalNonRelatedTransactions(index: number) {
		let total = 0;
		if (this.loanReportList[index].nonCashbookRelatedTransactions.length > 0) {
			for (let j = 0; j < this.loanReportList[index].nonCashbookRelatedTransactions.length; j++) total += this.loanReportList[index].nonCashbookRelatedTransactions[j].Amount;
		}

		return this.sharedService.roundTo2Dec(total);
	}

	getLastBalance(companyId: number) {
		this.totalCashBalance = 0;
		if (this.startDate != undefined) var yesterdayReducedASec = new Date(Number(this.startDate) - 1);
		const yesterdayStr = this.startDate == undefined ? undefined : yesterdayReducedASec.toISOString();

		const xhttp = this.restapi.getRequest(this.constructApi.getAccountsOfCompany(companyId, yesterdayStr));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);

				jsonArray.forEach(json => {
					const amount = json.balance == undefined ? 0 : json.balance;

					this.totalCashBalance += amount;
				});
			}
		};
	}

	getLoansTransaction() {
		this.loanReportList.splice(0, this.loanReportList.length);
		const startDateStr = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).toISOString();

		if (this.startDate != undefined)
			//in order to get the correct result, end date need to add 1 day and minus 1 millisecond
			// 3600*1000*24 is 1 day in millisecond, then reduced by 1 millisecond
			var addADayDate = new Date(Number(this.startDate) + 3600 * 1000 * 24 - 1);
		const endDateStr = addADayDate.toISOString();

		const xhttp = this.restapi.getRequest(this.constructApi.getCompanyAllLoansTransactions(this.selectedCompany, startDateStr, endDateStr));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);
				jsonArray.forEach(json => {
					//check if the transaction type = DUIT LEBIH (EXTRAS), if YES mark whether is positive or negative
					const isExtra = json.type == this.enums.EXTRAS;
					const isCharges =
						json.type == this.enums.STAMP_HQ ||
						json.type == this.enums.STAMP_OFFICE ||
						json.type == this.enums.STAMP ||
						json.type == this.enums.SERVICE_CHARGE ||
						json.type == this.enums.HANDLING_CHARGE;
					const isCashbookRelated =
						json.type == this.enums.REPAYMENT ||
						json.type == this.enums.PRINCIPAL ||
						json.type == this.enums.EXTRA_INTEREST ||
						json.type == this.enums.ARREAR_CHARGES ||
						json.type == this.enums.MONTHLY_INTEREST ||
						json.type == this.enums.PROCESSING_CHARGE ||
						json.type == this.enums.DISCOUNT;

					//find if profile existed in list
					const index = this.loanReportList.length > 0 ? this.loanReportList.findIndex(report => report.profileId === json.profileId) : -1;

					//If existed
					if (index != -1) {
						//if this transcation is EXTRAS, mark YES.
						//if not EXTRAS, dont change anything
						if (isExtra == true) {
							this.loanReportList[index].containsExtra = isExtra;
							this.loanReportList[index].extrasAmount += Number(json.amount);
						}

						this.loanReportList[index].amount += Number(json.amount);

						if (isCharges == true) this.loanReportList[index].chargesAmount += Number(json.amount);
						else if (isCashbookRelated == true) this.loanReportList[index].cashbookRelatedAmount += Number(json.amount);
						else
							this.loanReportList[index].nonCashbookRelatedTransactions.push(
								new Transaction(undefined, undefined, json.amount, undefined, undefined, undefined, json.type, undefined)
							);
					}

					//If its new record
					else {
						//add new record
						const extraAmount = isExtra == true ? json.amount : 0;
						const chargesAmount = isCharges == true ? json.amount : 0;
						const cashbookRelatedAmount = isCashbookRelated == true ? json.amount : 0;
						let nonCashbookRelatedTransactions;
						if (isCashbookRelated == false)
							nonCashbookRelatedTransactions = [new Transaction(undefined, undefined, json.amount, undefined, undefined, undefined, json.type, undefined)];
						else nonCashbookRelatedTransactions = [];

						const loanRecord = {
							date: json.date,
							remark: json.remark,
							amount: json.amount,
							receiptNumber: json.receiptNumber,
							receiptId: json.receiptId,
							type: json.type,
							customerName: json.customerName,
							customerCode: json.customerCode,
							customerId: json.customerId,
							profileId: json.profileId,
							containsExtra: isExtra,
							extrasAmount: extraAmount,
							chargesAmount: chargesAmount,
							cashbookRelatedAmount: cashbookRelatedAmount,
							nonCashbookRelatedTransactions: nonCashbookRelatedTransactions
						};
						this.loanReportList.push(loanRecord);
					}
				});

				this.getReserveTransactions();
			}
		};
	}

	getReserveTransactions() {
		const startDateStr = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).toISOString();

		if (this.startDate != undefined)
			//in order to get the correct result, end date need to add 1 day and minus 1 millisecond
			// 3600*1000*24 is 1 day in millisecond, then reduced by 1 millisecond
			var addADayDate = new Date(Number(this.startDate) + 3600 * 1000 * 24 - 1);
		const endDateStr = this.startDate == undefined ? undefined : addADayDate.toISOString();

		const xhttp = this.restapi.getRequest(this.constructApi.getCompanyReserveReceipts(this.selectedCompany, startDateStr, endDateStr));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);
				jsonArray.forEach(json => {
					const index = this.loanReportList.length > 0 ? this.loanReportList.findIndex(report => report.profileId === json.profileId) : -1;
					if (index != -1) {
						var reserveAmount = this.processReserveData(json.transactions);
						this.loanReportList[index].amount += reserveAmount;
						this.loanReportList[index].nonCashbookRelatedTransactions.push(
							new Transaction(undefined, undefined, reserveAmount, undefined, undefined, undefined, this.enums.RESERVE, undefined)
						);
					} else {
						var reserveAmount = this.processReserveData(json.transactions);
						const nonCashbookRelatedTransactions = new Transaction(undefined, undefined, reserveAmount, undefined, undefined, undefined, this.enums.RESERVE, undefined);
						const loanRecord = {
							amount: reserveAmount,
							customerName: json.name,
							customerCode: json.customerCode,
							customerId: json.customerId,
							profileId: json.profileId,
							chargesAmount: 0,
							nonCashbookRelatedTransactions: [nonCashbookRelatedTransactions]
						};
						this.loanReportList.push(loanRecord);
					}
				});

				this.getBDReserveTransactions();
			}
		};
	}

	getBDReserveTransactions() {
		const startDateStr = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).toISOString();

		if (this.startDate != undefined)
			//in order to get the correct result, end date need to add 1 day and minus 1 millisecond
			// 3600*1000*24 is 1 day in millisecond, then reduced by 1 millisecond
			var addADayDate = new Date(Number(this.startDate) + 3600 * 1000 * 24 - 1);
		const endDateStr = this.startDate == undefined ? undefined : addADayDate.toISOString();

		const xhttp = this.restapi.getRequest(this.constructApi.getCompanyBDReserveReceipts(this.selectedCompany, startDateStr, endDateStr));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);
				jsonArray.forEach(json => {
					const index = this.loanReportList.length > 0 ? this.loanReportList.findIndex(report => report.profileId === json.profileId) : -1;
					if (index != -1) {
						var reserveAmount = this.processReserveData(json.transactions);
						this.loanReportList[index].amount += reserveAmount;
						this.loanReportList[index].nonCashbookRelatedTransactions.push(
							new Transaction(undefined, undefined, reserveAmount, undefined, undefined, undefined, this.enums.BAD_DEBT, undefined)
						);
					} else {
						var reserveAmount = this.processReserveData(json.transactions);
						const nonCashbookRelatedTransactions = new Transaction(undefined, undefined, reserveAmount, undefined, undefined, undefined, this.enums.BAD_DEBT, undefined);
						const loanRecord = {
							amount: reserveAmount,
							customerName: json.name,
							customerCode: json.customerCode,
							customerId: json.customerId,
							profileId: json.profileId,
							nonCashbookRelatedTransactions: [nonCashbookRelatedTransactions]
						};
						this.loanReportList.push(loanRecord);
					}
				});
			}
		};
	}

	processReserveData(jsonTransactions: any) {
		let amount = 0;
		jsonTransactions.forEach(json => {
			amount += Number(json.amount);
		});
		return amount;
	}

	getOtherTranscations() {
		this.reportList.splice(0, this.reportList.length);
		const startDateStr = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).toISOString();

		if (this.startDate != undefined)
			//in order to get the correct result, end date need to add 1 day and minus 1 millisecond
			// 3600*1000*24 is 1 day in millisecond, then reduced by 1 millisecond
			var addADayDate = new Date(Number(this.startDate) + 3600 * 1000 * 24 - 1);
		const endDateStr = this.startDate == undefined ? undefined : addADayDate.toISOString();

		const types = [
			this.enums.STAMP,
			this.enums.SERVICE_CHARGE,
			this.enums.EXTRAS,
			this.enums.ASSET,
			this.enums.ADVANCE,
			this.enums.TRANSFER,
			this.enums.REALLOCATION,
			this.enums.EXPENSE,
			this.enums.CAPITAL,
			this.enums.SAVING,
			this.enums.TOTING,
			this.enums.BOUNTY,
			this.enums.HANDLING_CHARGE,
			this.enums.STAMP_HQ,
			this.enums.STAMP_OFFICE
		];
		const xhttp = this.restapi.getRequest(this.constructApi.getAllTransactions(this.selectedCompany, types, startDateStr, endDateStr));
		xhttp.onreadystatechange = () => {
			if (xhttp.readyState == 4 && xhttp.status == 200) {
				const jsonArray = JSON.parse(xhttp.responseText);

				jsonArray.forEach(json => {
					if (
						Number(json.type) == this.enums.SERVICE_CHARGE ||
						json.type == this.enums.STAMP ||
						json.type == this.enums.EXTRAS ||
						json.type == this.enums.HANDLING_CHARGE ||
						json.type == this.enums.STAMP_HQ ||
						json.type == this.enums.STAMP_OFFICE
					) {
						//normally for the type listed above, all the transactions need to link loan except for withdrawal
						//so most of the time only need to get -ve value that's not linking loan since is withdrwal
						//but, there are cases where we can convert STAMP HQ -> STAMP OFFICE, which makes the income of STAMP OFFICE without loan linkage
						//so at this part need to GET either -VE AMOUNT or STAMP OFFICE TYPE
						if (json.loanId == undefined) {
							if (Number(json.amount < 0) || json.type == this.enums.STAMP_OFFICE) {
								var record = { remark: json.remark, amount: json.amount, type: json.type, transactionId: json.id, date: json.date, groupId: json.groupId, setId: json.setId };
								this.reportList.push(record);
							}
						}
					} else {
						var record = { remark: json.remark, amount: json.amount, type: json.type, transactionId: json.id, date: json.date, groupId: json.groupId, setId: json.setId };
						this.reportList.push(record);
					}
				});
				this.reportList.sort((a, b) => (b.type > a.type ? 1 : -1));
			}
		};
	}
	openSetDialog(setID: number) {
		const modalRef = this.sharedService.openModal(ReceiptDetailsComponent);
		modalRef.componentInstance.receiptId = setID;
		modalRef.componentInstance.isModal = true;
		modalRef.componentInstance.passEntry.subscribe(entry => {
			window.location.reload();
		});
	}

	openDeleteTransactionDialog(transaction: ReportRecord) {
		const timeDiff = new Date().getTime() - new Date(transaction.date).getTime();
		const dateDiff = timeDiff / (1000 * 3600 * 24);
		if (dateDiff < 90 || this.sharedService.isBossAccount() == true) {
			const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
			modalRef.componentInstance.message = 'Confirm to delete transaction amount = RM ' + transaction.amount + ' and remark=' + transaction.remark + '?';

			modalRef.result.then(confirm => {
				if (confirm) {
					const modalRef = this.sharedService.openModal(DeleteReasonRemarkComponent);
					const transactionObj = new Transaction(
						transaction.transactionId,
						transaction.remark,
						transaction.amount,
						undefined,
						undefined,
						transaction.date,
						transaction.type,
						undefined,
						transaction.receiptId,
						transaction.customerId,
						undefined,
						undefined,
						undefined,
						undefined,
						transaction.groupId
					);

					modalRef.componentInstance.transaction = transactionObj;
					modalRef.componentInstance.companyId = this.selectedCompany;
					modalRef.componentInstance.groupId = transaction.groupId;
					modalRef.componentInstance.passEntry.subscribe(entry => {
						const remark = entry;

						//append added account to account list

						if (transaction.groupId == undefined) this.deleteTransaction(remark, transaction.transactionId, transaction.type);
						else this.deleteGroupTransaction(remark, transaction.groupId);
					});
				}
			});
		} else this.sharedService.openAlert('This transaction is too old, please contact your admin to delete', ALERT_TYPE.DANGER);
	}

	deleteGroupTransaction(remark: string, groupId: number) {
		const data = {
			reason: remark
		};

		const xhr = this.restapi.deleteRequest(this.constructApi.getDeleteGroupTransaction(this.selectedCompany, groupId), data);

		xhr.onreadystatechange = () => {
			if (xhr.readyState == 4) {
				if (xhr.status == 200) {
					window.location.reload();
				} else {
					// this.sharedService.openAlert("Opps, some errors occured. Please try again later");
					this.sharedService.openErrorMessage(xhr);
				}
			}
		};
	}
	deleteTransaction(remark: string, transactionID: number, transactionType: number) {
		const data = {
			reason: remark
		};

		let xhr;
		switch (transactionType) {
			case this.enums.ADVANCE:
				xhr = this.restapi.deleteRequest(this.constructApi.getDeleteAdvanceTransaction(transactionID), data);
				break;
			case this.enums.ASSET:
				xhr = this.restapi.deleteRequest(this.constructApi.getDeleteAssetTransaction(transactionID), data);
				break;
			default:
				xhr = this.restapi.deleteRequest(this.constructApi.getDeleteTransaction(transactionID), data);
				break;
		}

		xhr.onreadystatechange = () => {
			if (xhr.readyState == 4) {
				if (xhr.status == 200) {
					window.location.reload();
				} else {
					// this.sharedService.openAlert("Opps, some errors occured. Please try again later");
					this.sharedService.openErrorMessage(xhr);
				}
			}
		};
	}

	addThisTransactionIntoNewSet(transaction: ReportRecord) {
		const modalRef = this.sharedService.openModal(AddReceiptSetComponent);
		modalRef.componentInstance.transactionIDList = [transaction.transactionId];
		modalRef.componentInstance.startDate = this.startDate;
	}
}
